/**
 * Formato das regras de cada protocolo clínico (tabela `protocolos_clinicos`, migration 0010).
 * O JSON vem do banco; estes schemas conferem que ele está completo antes de o motor usá-lo.
 */
import { z } from 'zod';

export const ESTAGIOS_SIFILIS = [
  'primaria',
  'secundaria',
  'latente_recente',
  'latente_tardia',
  'terciaria',
  'ignorado',
] as const;
export type EstagioSifilis = (typeof ESTAGIOS_SIFILIS)[number];
export type EstagioDefinido = Exclude<EstagioSifilis, 'ignorado'>;

export const ROTULO_ESTAGIO: Record<EstagioSifilis, string> = {
  primaria: 'primária',
  secundaria: 'secundária',
  latente_recente: 'latente recente',
  latente_tardia: 'latente tardia',
  terciaria: 'terciária',
  ignorado: 'ignorado',
};

const doses = z.number().int().min(1).max(10);
const inteiroPositivo = z.number().int().positive();

/** D-001 — tratamento da sífilis na gestante e no parceiro. */
export const regrasTratamentoAdultoSchema = z.object({
  medicamento: z.string().min(1),
  dose_ui: inteiroPositivo,
  via: z.string().min(1),
  intervalo_dias: inteiroPositivo,
  tolerancia_horas: z.number().int().min(0),
  ao_exceder_tolerancia: z.literal('reiniciar_tratamento'),
  doses_por_estagio: z.object({
    primaria: doses,
    secundaria: doses,
    latente_recente: doses,
    latente_tardia: doses,
    terciaria: doses,
  }),
  /**
   * Estágio ignorado: a qual estágio equivale para o nº de doses.
   * ❓ Proposta (plano do Módulo 3, §6.1): latente tardia, como no PCDT para duração ignorada.
   * A versão 2026.1 não traz o campo; vale o padrão até o corpo clínico confirmar.
   */
  estagio_ignorado_equivale: z
    .enum(['primaria', 'secundaria', 'latente_recente', 'latente_tardia', 'terciaria'])
    .default('latente_tardia'),
  sinan_classificacao_clinica: z.record(z.enum(ESTAGIOS_SIFILIS), z.number().int()),
  sinan_esquema_por_doses: z.record(z.string(), z.number().int()),
});
export type RegrasTratamentoAdulto = z.infer<typeof regrasTratamentoAdultoSchema>;

/** D-004 — tratamento materno adequado (ficha SINAN de sífilis congênita, item 44). */
export const regrasAdequacaoSchema = z.object({
  medicamento_exigido: z.string().min(1),
  exige_doses_completas_para_estagio: z.boolean(),
  exige_respeitar_janela_entre_doses: z.boolean(),
  dias_minimos_inicio_antes_do_parto: z.number().int().min(0),
  considera_tratamento_parceiro: z.boolean(),
});
export type RegrasAdequacao = z.infer<typeof regrasAdequacaoSchema>;

export const EXAMES_RN = ['hemograma', 'glicemia', 'rx_ossos_longos', 'liquor'] as const;

/** D-002 — fluxograma de conduta no recém-nascido. */
export const regrasCondutaRnSchema = z.object({
  diferenca_minima_diluicoes: inteiroPositivo,
  benzatina_rn_ui_kg: inteiroPositivo,
  cristalina_ou_procaina_dias: inteiroPositivo,
  exames_mae_inadequada: z.array(z.enum(EXAMES_RN)).min(1),
  decisao_final: z.literal('medico'),
  divergencia_exige_justificativa: z.boolean(),
  /**
   * D-058: limites do líquor para sugerir "alterado" (PCDT IST, neurossífilis). ❓ A confirmar pelo
   * corpo clínico. A versão 2026.1 não traz o campo; vale o padrão.
   */
  liquor_limites: z
    .object({
      neonatal_ate_dias: inteiroPositivo,
      leucocitos_mm3: z.object({ neonatal: inteiroPositivo, depois: inteiroPositivo }),
      proteinas_mg_dl: z.object({ neonatal: inteiroPositivo, depois: inteiroPositivo }),
      vdrl_reagente_altera: z.boolean(),
    })
    .default({
      neonatal_ate_dias: 28,
      leucocitos_mm3: { neonatal: 25, depois: 5 },
      proteinas_mg_dl: { neonatal: 150, depois: 40 },
      vdrl_reagente_altera: true,
    }),
});
export type RegrasCondutaRn = z.infer<typeof regrasCondutaRnSchema>;

/** D-003 — seguimento da criança exposta. */
export const regrasSeguimentoSchema = z.object({
  teste_nao_treponemico_meses: z.array(inteiroPositivo).min(1),
  teste_treponemico_a_partir_meses: inteiroPositivo,
  consultas: z
    .array(z.object({ ate_meses: inteiroPositivo, intervalo_meses: inteiroPositivo }))
    .min(1),
  encaminhamentos_iniciais: z.array(z.string()),
  encaminhamento_condicional: z.record(z.string(), z.string()),
  alertas: z.object({
    titulo_ascendente_diluicoes: inteiroPositivo,
    reagente_apos_meses_em_tratado: inteiroPositivo,
    treponemico_reagente_apos_meses: inteiroPositivo,
  }),
  coleta_12_meses_cancelavel_com_justificativa: z.boolean(),
  /**
   * D-044: sem nenhum registro (coleta ou consulta) há este número de meses → sugerir "perda de
   * seguimento". ❓ Proposta (plano do Módulo 7, §8). A versão 2026.1 não traz o campo; vale o padrão.
   */
  perda_seguimento_meses: inteiroPositivo.default(6),
});
export type RegrasSeguimento = z.infer<typeof regrasSeguimentoSchema>;

/** D-029 (proposta) — monitoramento da gestante após o tratamento (controle de cura). */
export const regrasMonitoramentoGestanteSchema = z.object({
  testes: z.array(z.enum(['vdrl', 'rpr'])).min(1),
  intervalo_meses: inteiroPositivo,
  ate: z.literal('parto'),
  alerta_subida_diluicoes: inteiroPositivo,
  referencia: z.literal('menor_titulo_apos_tratamento'),
});
export type RegrasMonitoramentoGestante = z.infer<typeof regrasMonitoramentoGestanteSchema>;

/** D-029 (proposta) — parceiro de gestante com sífilis e teste não reagente. */
export const regrasParceiroPresuntivoSchema = z.object({
  sugerir_tratamento_se_nao_reagente: z.boolean(),
  doses_presuntivas: doses,
  motivo_sinan_se_nao_tratar: z.number().int(),
});
export type RegrasParceiroPresuntivo = z.infer<typeof regrasParceiroPresuntivoSchema>;

/** Código do protocolo (coluna `codigo`) → schema das regras. */
export const SCHEMAS_PROTOCOLO = {
  sifilis_tratamento_adulto: regrasTratamentoAdultoSchema,
  sifilis_tratamento_adequado: regrasAdequacaoSchema,
  sifilis_conduta_rn: regrasCondutaRnSchema,
  sifilis_seguimento_crianca: regrasSeguimentoSchema,
  sifilis_monitoramento_gestante: regrasMonitoramentoGestanteSchema,
  sifilis_parceiro_presuntivo: regrasParceiroPresuntivoSchema,
} as const;
export type CodigoProtocolo = keyof typeof SCHEMAS_PROTOCOLO;
export const CODIGOS_PROTOCOLO = Object.keys(SCHEMAS_PROTOCOLO) as CodigoProtocolo[];

export interface RegrasPorCodigo {
  sifilis_tratamento_adulto: RegrasTratamentoAdulto;
  sifilis_tratamento_adequado: RegrasAdequacao;
  sifilis_conduta_rn: RegrasCondutaRn;
  sifilis_seguimento_crianca: RegrasSeguimento;
  sifilis_monitoramento_gestante: RegrasMonitoramentoGestante;
  sifilis_parceiro_presuntivo: RegrasParceiroPresuntivo;
}

/** Confere o JSON de um protocolo. Lança erro com o caminho do campo inválido. */
export function lerRegras<C extends CodigoProtocolo>(
  codigo: C,
  regras: unknown,
): RegrasPorCodigo[C] {
  const resultado = SCHEMAS_PROTOCOLO[codigo].safeParse(regras);
  if (!resultado.success) {
    const problemas = resultado.error.issues
      .map((i) => `${i.path.join('.') || '(raiz)'}: ${i.message}`)
      .join('; ');
    throw new Error(`Regras do protocolo "${codigo}" inválidas — ${problemas}`);
  }
  return resultado.data as RegrasPorCodigo[C];
}
